import { Box, Card, Grid, Grow, IconButton, InputLabel, MenuItem, Typography } from '@material-ui/core'
import StandardTable from 'asterX-UI/Table/StandardTable'
import React, { useEffect, useRef, useState } from 'react'
import { useContractStore } from 'context/ContractStore'
import { NotFound } from 'components/NotFound/NotFound'
import ActionModal from 'asterX-UI/Modals/ActionModal'
import { SelectPlaceholder } from 'asterX-UI/Inputs'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { CollectionService } from 'lib/api/services/collection.service'
import { useSnackbar } from 'notistack'
import { FileIcon } from 'asterX-UI/Icons/FileIcon'
import { BlueLines } from 'asterX-UI/Icons/BlueLines'
import { DeleteIcon } from 'asterX-UI/Icons/DeleteIcon'
import { UploadIcon } from 'asterX-UI/Icons/UploadIcon'
import { useAuthStore } from 'context/AuthStore'
import { BorderLinearProgress } from '../FileContract/components/UploadFilesModal/const'
import { lawHistoryColumns, lawHistoryRows } from './const'
import { useStyles } from './law-history.styles'

export const LawHistory = () => {
  const classes = useStyles()
  const intervalRefs = useRef({})
  const fileInputRef = useRef(null)
  const { authStoreFunc } = useAuthStore()
  const { enqueueSnackbar } = useSnackbar()

  const {
    contractStore: { contractAll, lawyerEvents, lawyerStatuses },
    contractStoreFunc
  } = useContractStore()

  const [openModal, setOpenModal] = useState(false)
  const [currentRowData, setCurrentRowData] = useState(null)
  const [files, setFiles] = useState([])

  const selectedLawyerEvent = lawyerStatuses?.list?.find(item => item?.legalTypeCode === currentRowData?.type?.code)

  const tableData = {
    columns: lawHistoryColumns,
    rows: lawHistoryRows(contractAll?.legals)
  }

  useEffect(() => {
    contractStoreFunc.getLawyerStatuses()
  }, [])

  useEffect(() => {
    const currentIntervalRefs = intervalRefs.current

    return () => {
      Object.values(currentIntervalRefs).forEach(clearInterval)
    }
  }, [])

  const handleOpenRow = row => {
    const rowData = row.find(item => item?.isButton)
    const current = contractAll?.legals.find(e => e?.id === rowData?.data?.id)
    if (!current?.result && authStoreFunc.isLawyer()) {
      setCurrentRowData(current)
      setOpenModal(true)
    }
  }

  const handleClose = () => {
    setOpenModal(false)
    setFiles([])
    contractStoreFunc.clearLawyerEvents()
  }

  const handleUploadFile = async event => {
    const file = event.target.files?.[0]
    const data = new FormData()
    data.append('file', file)

    const res = await CollectionService.uploadFile(data)

    if (res?.data) {
      const newFile = {
        name: res?.data?.fileName,
        fileDownloadUri: res?.data?.fileDownloadUri,
        size: res?.data?.size,
        date: new Date().toLocaleDateString(),
        progress: 0
      }

      setFiles(prevFiles => [...prevFiles, newFile])

      intervalRefs.current[newFile.name] = setInterval(() => {
        setFiles(prevFiles => {
          const updatedFiles = prevFiles.map(f => {
            if (f.name === newFile.name) {
              const updatedProgress = f.progress + 20
              if (updatedProgress >= 100) {
                clearInterval(intervalRefs.current[newFile.name])
                delete intervalRefs.current[newFile.name]
                return { ...f, progress: 100 }
              }
              return { ...f, progress: updatedProgress }
            }
            return f
          })
          return updatedFiles
        })
      }, 100)
    }
  }

  const handleClick = () => {
    fileInputRef.current?.click()
  }

  const handleDelete = fileName => {
    clearInterval(intervalRefs.current[fileName])
    delete intervalRefs.current[fileName]
    setFiles(files.filter(file => file.name !== fileName))
  }

  const saveWorkListFile = async () => {
    const filesData = {
      workListId: contractAll?.id,
      fileType: lawyerEvents?.legalResultCode,
      downloadUrl: files?.[0]?.fileDownloadUri
    }

    await CollectionService.saveWlFile(filesData)
      .then(res => {
        if (res.data) {
          contractStoreFunc.getWlFiles(contractAll?.id)
        } else {
          enqueueSnackbar(`😿 Что-то пошло не так, статус ${res?.status}`, { variant: 'error' })
        }
      })
      .finally(() => {})
  }

  const handleSendLegalResultCode = async () => {
    const missingFields = []

    if (!lawyerEvents?.legalResultCode) {
      missingFields.push('Выбрать результат ')
    }

    if (lawyerEvents?.legalResultCode && !files?.[0]?.fileDownloadUri) {
      missingFields.push('Выберите файл')
    }

    if (missingFields.length > 0) {
      enqueueSnackbar(`😿 Заполните поле(я): ${missingFields.join(', ')}`, { variant: 'info' })
      return
    }

    const data = {
      id: Number(currentRowData?.id),
      legalResultCode: lawyerEvents.legalResultCode
    }

    await CollectionService.patchLawyerEvents(data)
      .then(res => {
        if (res.data) {
          enqueueSnackbar('Успешно 😸', { variant: 'success' })
          handleClose()
          if (files?.[0]?.fileDownloadUri) {
            saveWorkListFile()
          }
          contractStoreFunc.getById()
        } else {
          enqueueSnackbar(`😿 Что-то пошло не так, статус ${res?.status}`, { variant: 'error' })
          return
        }
      })
      .finally(() => {})
  }

  return (
    <Grid container className={contractAll?.legals?.length > 0 ? classes.wrapper : ''}>
      {contractAll?.legals?.length > 0 ? (
        <StandardTable
          tableData={tableData}
          onRowClick={handleOpenRow}
          btnStyle={{ backgroundColor: 'transparent', border: 'none', padding: 8, minWidth: 'auto' }}
        />
      ) : (
        <NotFound />
      )}

      <ActionModal
        open={openModal}
        onCancel={handleClose}
        onClose={handleClose}
        onConfirm={handleSendLegalResultCode}
        title="Добавить результат"
      >
        <Grid item container xs={12} className={classes.container}>
          <Grid item xs={12} className={classes.input}>
            <InputLabel shrink className={classes.inputLabel}>
              Выбрать результат *
            </InputLabel>
            <SelectPlaceholder
              IconComponent={props => <ExpandMoreIcon {...props} style={{ margin: '0 8px 0 8px' }} />}
              value={lawyerEvents?.legalResultCode || null}
              name={'legalResultCode'}
              onChange={e => contractStoreFunc.handleLawyerEventsChange(e)}
              fullWidth
              placeholder={'Результат'}
            >
              <MenuItem value={''}>Не выбрано</MenuItem>
              {(selectedLawyerEvent?.legalResultList || []).map(result => (
                <MenuItem key={result?.code} value={result?.code}>
                  {result?.name}
                </MenuItem>
              ))}
            </SelectPlaceholder>
          </Grid>
          {lawyerEvents?.legalResultCode && (
            <>
              <Card variant="outlined" className={classes.card}>
                <IconButton className={classes.icon} component="label">
                  <UploadIcon />
                  <input
                    type="file"
                    hidden
                    onChange={handleUploadFile}
                    ref={fileInputRef}
                    disabled={files?.length === 1}
                  />
                </IconButton>
                <Grid item className={classes.textWrapper} onClick={handleClick}>
                  <Typography className={files?.length === 1 ? classes.disabledTitle : classes.title}>
                    Нажмите, чтобы загрузить
                  </Typography>
                  <Typography className={classes.subtitle}>или перетащите документ в эту область</Typography>
                </Grid>
              </Card>

              {(files || []).map(file => (
                <Grow key={file.name} in={file.name} timeout={500}>
                  <Grid item container className={classes.fileContainer}>
                    <Grid item className={classes.fileIcon}>
                      <FileIcon />
                      <Box className={classes.blueLines}>
                        <BlueLines />
                      </Box>
                    </Grid>
                    <Grid item xs={10}>
                      <Grid item className={classes.fileWrapper}>
                        <Grid item>
                          <Typography variant={'body2'} className={classes.label}>
                            {file.name}
                          </Typography>
                          <Typography variant={'body2'} className={classes.date}>
                            {file.date}
                          </Typography>
                        </Grid>
                        <IconButton onClick={() => handleDelete(file.name)}>
                          <DeleteIcon className={classes.actionBtn} />
                        </IconButton>
                      </Grid>
                      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <Box width="100%" mr={1}>
                          <BorderLinearProgress variant="determinate" value={file.progress} />
                        </Box>
                        <Box minWidth={35}>
                          <Typography variant="body2">{`${Math.round(file.progress)}%`}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Grow>
              ))}
            </>
          )}
        </Grid>
      </ActionModal>
    </Grid>
  )
}
